import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { Box, TextField, Typography } from '@mui/material';
import { createUserAndLogin, generateToken } from '@tailored-pdf/api';
import { useFormik } from 'formik';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabeledField } from './LabeledField';
import { LoginButton } from './LoginButton';
import { fireSignupConversion, logEvent } from '@/helpers/eventLogger';

type LoginFormDataType = {
  email: string;
  password: string;
};

function EmailForm() {
  const { t } = useTranslation('web', { keyPrefix: 'components.AuthModal' });
  const {
    state: { authState },
    dispatch,
  } = useContext(Context);

  const { notifyError } = useNotifications();
  const [loading, setLoading] = useState(false);

  const handleLogin = useCallback(
    async (data: LoginFormDataType) => {
      logEvent({ logType: 'EVENT', eventType: 'USER_SIGN_IN' });
      setLoading(true);
      try {
        const resp = await generateToken(data);
        dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: { token: resp.data, source: 'SIGN_IN' } });
        dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: {} });
      } catch (e: unknown) {
        logEvent({ logType: 'ERROR', eventType: 'USER_SIGN_IN_ERROR', trace: e });
        notifyError(getResponseError(e));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, notifyError],
  );

  const handleSendEmail = useCallback(
    async (data: LoginFormDataType) => {
      logEvent({ logType: 'EVENT', eventType: 'USER_SIGN_UP' });
      fireSignupConversion();
      setLoading(true);
      try {
        const resp = await createUserAndLogin(data);
        dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: { token: resp.data, source: 'SIGN_UP' } });
        dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: {} });
      } catch (e: unknown) {
        logEvent({ logType: 'ERROR', eventType: 'USER_SIGN_UP_ERROR', trace: e });
        notifyError(getResponseError(e));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, notifyError],
  );

  const handleRestore = useCallback(() => {
    dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: { state: 'RESTORE' } });
  }, [dispatch]);

  const loginForm = useFormik<LoginFormDataType>({
    initialValues: { email: '', password: '' },
    onSubmit: handleLogin,
  });

  const emailForm = useFormik<LoginFormDataType>({
    initialValues: { email: '', password: '' },
    onSubmit: handleSendEmail,
  });

  return authState.state === 'SIGN_IN' ? (
    <form onSubmit={loginForm.handleSubmit}>
      <Box gap={4} display={'flex'} flexDirection={'column'}>
        <LabeledField label={t('emailLabel')}>
          <TextField
            required
            name="email"
            type="email"
            placeholder={t('emailPlaceholder')}
            variant="outlined"
            size="small"
            value={loginForm.values.email}
            onChange={loginForm.handleChange}
          />
        </LabeledField>
        <LabeledField label={t('passwordLabel')}>
          <TextField
            required
            name="password"
            type="password"
            placeholder={t('passwordPlaceholder')}
            variant="outlined"
            size="small"
            value={loginForm.values.password}
            onChange={loginForm.handleChange}
          />
          <Typography sx={{ cursor: 'pointer', color: 'primary.main', fontSize: '12px' }} onClick={handleRestore}>
            {t('forgotPassword')}
          </Typography>
        </LabeledField>
        <LoginButton loading={loading}>{t('logInWithEmail')}</LoginButton>
      </Box>
    </form>
  ) : (
    <form onSubmit={emailForm.handleSubmit}>
      <Box gap={4} display={'flex'} flexDirection={'column'}>
        <LabeledField label={t('emailLabel')}>
          <TextField
            required
            name="email"
            type="email"
            placeholder={t('emailPlaceholder')}
            variant="outlined"
            size="small"
            value={emailForm.values.email}
            onChange={emailForm.handleChange}
          />
        </LabeledField>
        <LabeledField label={t('passwordLabel')}>
          <TextField
            required
            name="password"
            type="password"
            placeholder={t('passwordPlaceholder')}
            variant="outlined"
            size="small"
            value={emailForm.values.password}
            onChange={emailForm.handleChange}
          />
          <Typography fontSize={'12px'} color={'text.secondary'}>
            {t('passwordInfo')}
          </Typography>
        </LabeledField>
        <LoginButton loading={loading}>{t('continueWithEmail')}</LoginButton>
      </Box>
    </form>
  );
}

export { EmailForm };
